import type { ModelData } from '@/types/models';
import { formatCurrency } from '@/utils/formatters';

interface BalanceSheetTabProps {
  data: ModelData;
}

interface BalanceSheetRow {
  key: string;
  label: string;
  indent: number;
  bold?: boolean;
  highlight?: boolean;
  primary?: boolean;
}

export function BalanceSheetTab({ data }: BalanceSheetTabProps) {
  const years = data?.years || [];
  const balanceSheet = data?.balanceSheet || [];

  // Handle case where data is not yet loaded
  if (!years.length || !balanceSheet.length) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-4xl mb-3">📋</div>
          <p className="text-slate-500">No balance sheet data available</p>
        </div>
      </div>
    );
  }

  const sections: { title: string; rows: BalanceSheetRow[] }[] = [
    {
      title: 'ASSETS',
      rows: [
        { key: 'cash', label: 'Cash', indent: 1 },
        { key: 'accountsReceivable', label: 'Accounts Receivable', indent: 1 },
        { key: 'inventory', label: 'Inventory', indent: 1 },
        { key: 'prepaidExpenses', label: 'Prepaid Expenses', indent: 1 },
        { key: 'totalCurrentAssets', label: 'Total Current Assets', indent: 0, bold: true, highlight: true },
        { key: 'netFixedAssets', label: 'Net Fixed Assets', indent: 1 },
        { key: 'totalAssets', label: 'TOTAL ASSETS', indent: 0, bold: true, primary: true },
      ],
    },
    {
      title: 'LIABILITIES',
      rows: [
        { key: 'accountsPayable', label: 'Accounts Payable', indent: 1 },
        { key: 'accruedExpenses', label: 'Accrued Expenses', indent: 1 },
        { key: 'totalCurrentLiabilities', label: 'Total Current Liabilities', indent: 0, bold: true, highlight: true },
        { key: 'longTermDebt', label: 'Long-Term Debt', indent: 1 },
        { key: 'totalLiabilities', label: 'Total Liabilities', indent: 0, bold: true },
      ],
    },
    {
      title: 'EQUITY',
      rows: [
        { key: 'paidInCapital', label: 'Paid-In Capital', indent: 1 },
        { key: 'retainedEarnings', label: 'Retained Earnings', indent: 1 },
        { key: 'totalEquity', label: 'Total Equity', indent: 0, bold: true, highlight: true },
      ],
    },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-3 px-4 font-semibold text-slate-700 bg-slate-50">
              Balance Sheet Item
            </th>
            {years.map((year) => (
              <th
                key={year}
                className="text-right py-3 px-4 font-semibold text-slate-700 bg-slate-50"
              >
                Year {year}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {sections.map((section) => (
            <>
              <tr key={section.title} className="bg-slate-100">
                <td
                  colSpan={years.length + 1}
                  className="py-2 px-4 font-bold text-slate-700 text-center"
                >
                  {section.title}
                </td>
              </tr>
              {section.rows.map((row) => (
                <tr
                  key={row.key}
                  className={`hover:bg-slate-50 ${
                    row.primary
                      ? 'bg-primary-50 border-t-2 border-b-2 border-slate-300'
                      : row.highlight
                      ? 'bg-slate-50'
                      : ''
                  }`}
                >
                  <td
                    className={`py-3 px-4 ${row.bold ? 'font-semibold' : ''} ${
                      row.primary ? 'text-primary-900' : 'text-slate-900'
                    }`}
                    style={{ paddingLeft: `${16 + (row.indent || 0) * 16}px` }}
                  >
                    {row.label}
                  </td>
                  {balanceSheet.map((yearData, i) => {
                    const value = (yearData?.[row.key as keyof typeof yearData] as number) || 0;
                    const isNegative = value < 0;
                    return (
                      <td
                        key={i}
                        className={`py-3 px-4 text-right ${
                          row.bold ? 'font-semibold' : ''
                        } ${
                          row.primary
                            ? 'text-primary-900'
                            : isNegative
                            ? 'text-red-600'
                            : 'text-slate-700'
                        }`}
                      >
                        {formatCurrency(value)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </>
          ))}
          
          {/* Balance Check */}
          <tr className="border-t-2 border-slate-300 bg-green-50">
            <td className="py-3 px-4 font-bold text-green-800">
              ✓ Balance Check (Assets - Liabilities - Equity)
            </td>
            {balanceSheet.map((yearData, i) => {
              const balanceCheck = yearData?.balanceCheck ?? 
                ((yearData?.totalAssets || 0) - (yearData?.totalLiabilities || 0) - (yearData?.totalEquity || 0));
              const isBalanced = Math.abs(balanceCheck) < 1; // Allow small rounding errors
              return (
                <td
                  key={i}
                  className={`py-3 px-4 text-right font-bold ${
                    isBalanced ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {isBalanced ? '✓ Balanced' : formatCurrency(balanceCheck)}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
